
import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { SubscriptionStatus, UseSubscriptionResult } from "./types";
import { checkSubscriptionStatus, createCheckoutSession } from "./api";
import { getDefaultSubscriptionStatus, shouldDeferCheck, determineSubscriptionStatus } from "./utils";
import useFacebookConversions from "@/hooks/useFacebookConversions";

export const useSubscription = (): UseSubscriptionResult => {
  const { user } = useAuth();
  const [status, setStatus] = useState<SubscriptionStatus>(getDefaultSubscriptionStatus());
  const { trackEvent } = useFacebookConversions();
  
  const checkInProgress = useRef(false);
  const checkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCheckRef = useRef<number>(0);

  useEffect(() => {
    const checkForPaymentSuccess = async () => {
      const url = new URL(window.location.href);
      const isSuccessfulPayment = url.searchParams.get('payment_success') === 'true';
      
      if (isSuccessfulPayment) {
        url.searchParams.delete('payment_success');
        window.history.replaceState({}, '', url.toString());
        
        if (user) {
          // Track successful purchase event 
          await trackEvent("Purchase", {
            customData: {
              value: status.subscriptions?.[0]?.plan === "RaiseSpark Pro" ? 997 : 497,
              currency: "USD",
              contentName: status.subscriptions?.[0]?.plan || "Subscription"
            }
          });

          checkSubscription();
          toast.success("Payment successful! Your subscription is now active.");
        }
      }
    };
    
    checkForPaymentSuccess();
  }, [user, window.location.href]);

  const checkSubscription = useCallback(async (priceId?: string): Promise<SubscriptionStatus> => {
    if (!user) {
      const noUserStatus = {
        ...getDefaultSubscriptionStatus(),
        loading: false
      };
      setStatus(noUserStatus);
      return noUserStatus;
    }
    
    const now = Date.now();
    if (shouldDeferCheck(now, lastCheckRef.current, checkInProgress.current)) {
      console.log("Deferring subscription check");
      if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current);
      
      return new Promise((resolve) => {
        checkTimeoutRef.current = setTimeout(async () => {
          const result = await checkSubscription(priceId);
          resolve(result);
        }, 3000);
      });
    }
    
    console.log("Starting subscription check");
    checkInProgress.current = true;
    lastCheckRef.current = now;

    try {
      setStatus(prev => ({ ...prev, loading: true, error: null }));
      
      const updatedStatus = await checkSubscriptionStatus(priceId);
      console.log("Received subscription status:", updatedStatus);
      
      // Extra logging for debugging trial status
      console.log("Trial status from API:", updatedStatus.onTrial);
      console.log("Subscription data:", updatedStatus.subscriptions);
      
      // Check if any subscription has a trial end date in the future
      if (updatedStatus.subscriptions?.length > 0) {
        updatedStatus.subscriptions.forEach((sub, index) => {
          console.log(`Subscription ${index}:`, sub);
          if (sub.trialEnd) {
            console.log(`  Trial end: ${sub.trialEnd}`);
            console.log(`  Is future date: ${new Date(sub.trialEnd) > new Date()}`);
          }
          if (sub.onTrial) {
            console.log(`  Explicitly marked as trial: ${sub.onTrial}`);
          }
        });
      }
      
      setStatus(updatedStatus);
      return updatedStatus;
    } catch (error) {
      console.error("Error in checkSubscription:", error);
      
      // IMPORTANT: If we get an error, assume the user has access
      // This prevents blocking paid users if the API is down
      const errorStatus = { 
        ...getDefaultSubscriptionStatus(),
        loading: false, 
        error: error.message || "Unknown error checking subscription",
        // Default to providing access if we can't verify
        active: true,
        subscribed: true,
        onTrial: false
      };
      setStatus(errorStatus);
      return errorStatus;
    } finally {
      checkInProgress.current = false;
    }
  }, [user]);

  const startCheckout = useCallback(async (
    priceId: string, 
    options?: {
      isExtendedTrial?: boolean;
      discountPercentage?: number;
      successUrl?: string;
    } | string
  ): Promise<void> => {
    if (!user) {
      toast.error("Please log in to subscribe");
      return;
    }

    try {
      toast.loading("Preparing checkout...");
      
      // Track InitiateCheckout event when user starts checkout process
      await trackEvent("InitiateCheckout", {
        customData: {
          contentIds: [priceId],
          contentType: "product",
        }
      });
      
      // Handle the case where options is a string (backwards compatibility)
      let successUrl: string | undefined;
      let checkoutOptions: {
        isExtendedTrial?: boolean;
        discountPercentage?: number;
      } = {};
      
      if (typeof options === 'string') {
        successUrl = options || undefined;
      } else if (options) {
        // Extract successUrl from options object if provided
        successUrl = options.successUrl;
        checkoutOptions = {
          isExtendedTrial: options.isExtendedTrial,
          discountPercentage: options.discountPercentage
        };
      }
      
      // Check URL parameters for options
      const urlParams = new URLSearchParams(window.location.search);
      const hasExtendedParam = urlParams.get('extended') === 'true';
      const discountParam = urlParams.get('discount');
      
      // Use parameters from function or URL
      const useExtendedTrial = checkoutOptions.isExtendedTrial || hasExtendedParam;
      const useDiscount = checkoutOptions.discountPercentage || (discountParam ? parseInt(discountParam, 10) : undefined);
      
      console.log("Checkout options:", { useExtendedTrial, useDiscount, priceId, successUrl });
      
      const url = await createCheckoutSession(priceId, successUrl, {
        isExtendedTrial: useExtendedTrial,
        discountPercentage: useDiscount
      });
      
      console.log("Redirecting to checkout URL:", url);
      window.location.href = url;
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.message || "Checkout failed");
    }
  }, [user, trackEvent]);

  useEffect(() => {
    if (user) {
      console.log("User authenticated, checking subscription");
      checkSubscription();
    } else {
      console.log("No user, resetting subscription status");
      setStatus(prev => ({
        ...prev,
        loading: false,
        onTrial: false,
        active: false,
        subscribed: false
      }));
    }
  }, [user, checkSubscription]);

  // Use the enhanced subscription determination logic
  const isActuallySubscribed = determineSubscriptionStatus(status);

  return { 
    status, 
    checkSubscription,
    startCheckout,
    isSubscribed: isActuallySubscribed, 
    isLoading: status.loading
  };
};
